import { useCallback, useSyncExternalStore } from 'react';
import { useStore } from 'react-redux';

import { type Scopes, type User } from '../../auth';
import { type AppState } from '../../reducer';

/**
 * Hook to access auth data from Redux state.
 * Auth state is managed by the auth flow and should remain in Redux.
 *
 * BIS-3204: reads go through useSyncExternalStore rather than useSelector.
 * react-redux 7.2.2 predates concurrent rendering and can drop store
 * notifications under React 19; for most slices that is self-healing (the
 * next dispatch re-renders), but auth changes can be single-shot — a missed
 * SIGNED_IN or SIGNED_OUT with no later dispatch leaves consumers stale
 * indefinitely. Collapse back to useSelector once react-redux is on v9,
 * which is built on this same primitive.
 */
export function useAuth() {
  const store = useStore();
  const subscribe = useCallback((onChange: () => void) => store.subscribe(onChange), [store]);
  // Reference-stable: the auth reducer returns the existing state object for
  // unrelated actions, so getSnapshot only changes identity on auth changes.
  const getAuthSnapshot = useCallback(() => (store.getState() as AppState).auth, [store]);
  const { user, scopes, isReady } = useSyncExternalStore(subscribe, getAuthSnapshot, getAuthSnapshot);

  return {
    user: user as User | null,
    scopes: (scopes || {}) as Scopes, // Provide fallback for scopes
    isReady: isReady as boolean,
  };
}
